/**
 * Arrastar com o mouse para rolar na horizontal.
 *
 * Nasceu no quadro kanban e serve igual ao diagrama de automações: as duas
 * telas são largas, a TV da oficina não tem trackpad e a barra de rolagem no
 * rodapé exige mira. Clicar em qualquer espaço vazio e puxar é o gesto
 * esperado.
 *
 * Dois cuidados:
 *  • cartão de OS é `draggable`: se o gesto começar sobre ele, quem manda é o
 *    arraste nativo (mover de etapa) — o pan não pode competir;
 *  • só entra em modo pan depois de alguns pixels de movimento, senão o clique
 *    que abre o modal seria engolido.
 */
import { useRef, useState } from 'react';

const LIMIAR_PAN = 5; // px

export function usePanHorizontal() {
  const alvo = useRef<HTMLDivElement>(null);
  const gesto = useRef<{ id: number; x: number; scroll: number; ativo: boolean } | null>(null);
  const [arrastando, setArrastando] = useState(false);

  function encerrar(e: React.PointerEvent<HTMLDivElement>): void {
    const g = gesto.current;
    if (!g || g.id !== e.pointerId) return;
    gesto.current = null;
    setArrastando(false);
    if (alvo.current?.hasPointerCapture(e.pointerId)) {
      alvo.current.releasePointerCapture(e.pointerId);
    }
  }

  return {
    arrastando,
    props: {
      ref: alvo,
      onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
        // Só botão esquerdo; toque já rola sozinho.
        if (e.button !== 0 || e.pointerType === 'touch') return;
        const origem = e.target as HTMLElement;
        if (origem.closest('[draggable="true"], a, input, select, textarea')) return;

        gesto.current = {
          id: e.pointerId,
          x: e.clientX,
          scroll: alvo.current?.scrollLeft ?? 0,
          ativo: false,
        };
      },
      onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const g = gesto.current;
        if (!g || g.id !== e.pointerId) return;

        const deslocamento = e.clientX - g.x;
        if (!g.ativo) {
          if (Math.abs(deslocamento) < LIMIAR_PAN) return;
          g.ativo = true;
          setArrastando(true);
          alvo.current?.setPointerCapture(e.pointerId);
        }

        if (alvo.current) alvo.current.scrollLeft = g.scroll - deslocamento;
      },
      onPointerUp: encerrar,
      onPointerCancel: encerrar,
      onLostPointerCapture: encerrar,
    },
  };
}

